import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Wrench, ShieldWarning, ArrowsClockwise, Info } from "@phosphor-icons/react";

/**
 * AutoDiagnoseBanner — Claude Vision-driven diagnosis of WO photos.
 * Shows issue_type + severity chip + suggested trade + recommended actions.
 * If no diagnosis yet, offers a "Run AI Diagnosis" trigger (needs >=1 before-photo).
 */
export default function AutoDiagnoseBanner({ job, onReload, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const dx = job.ai_diagnosis || null;
  const hasPhotos = (job.before_photos || []).some(p => p.media_type !== "video");

  const run = async () => {
    if (!hasPhotos) { toast.error("Upload at least one before-photo first"); return; }
    setBusy(true);
    const tid = toast.loading("Claude is diagnosing this job…");
    try {
      const r = await api.post(`/jobs/${job.id}/auto-diagnose`, { apply_priority: true });
      toast.dismiss(tid);
      toast.success(`Diagnosed: ${r.data.ai_diagnosis?.issue_type || "issue"}${r.data.priority_bumped ? " · priority ↑" : ""}`);
      if (onReload) await onReload();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Diagnosis failed");
    } finally { setBusy(false); }
  };

  if (!dx) {
    if (disabled || !hasPhotos) return null;
    return (
      <button onClick={run} disabled={busy} data-testid="auto-diagnose-trigger"
        className="mt-3 w-full h-11 border-2 border-fuchsia-600 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-900 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50">
        <Sparkle weight="fill" size={16}/> {busy ? "Diagnosing…" : "Run AI Diagnosis on Photos"}
      </button>
    );
  }

  const sev = dx.severity || "normal";
  const sevMap = {
    urgent: { cls: "bg-red-600 text-white", border: "border-red-600", icon: "🚨" },
    high:   { cls: "bg-amber-600 text-white", border: "border-amber-600", icon: "⚠︎" },
    normal: { cls: "bg-slate-700 text-white", border: "border-slate-700", icon: "" },
    low:    { cls: "bg-emerald-600 text-white", border: "border-emerald-600", icon: "" },
  };
  const sevCfg = sevMap[sev] || sevMap.normal;
  const conf = typeof dx.confidence === "number" ? dx.confidence : 0;

  return (
    <div className={`mt-3 rugged-card p-4 bg-white border-l-4 ${sevCfg.border}`} data-testid="ai-diagnosis-banner">
      <div className="flex items-start gap-3">
        <Sparkle weight="fill" size={22} className="text-fuchsia-600 flex-shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700">AI Diagnosis</span>
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${sevCfg.cls}`}
              data-testid={`ai-severity-${sev}`}>
              {sevCfg.icon} {sev}
            </span>
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-cyan-700 text-white inline-flex items-center gap-1"
              data-testid={`ai-trade-${dx.suggested_trade}`}>
              <Wrench weight="fill" size={10}/> {dx.suggested_trade}
            </span>
            {conf > 0 && (
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="ai-confidence">
                {conf}% confidence
              </span>
            )}
          </div>
          <div className="mt-1 font-display font-black text-lg leading-tight" data-testid="ai-issue-type">
            {dx.issue_type || "Untitled issue"}
          </div>
          {dx.summary && (
            <div className="mt-1 text-sm text-slate-700" data-testid="ai-summary">
              {dx.summary}
            </div>
          )}

          {(dx.recommended_actions || []).length > 0 && (
            <div className="mt-2" data-testid="ai-actions-list">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Recommended actions</div>
              <ul className="mt-1 space-y-0.5">
                {dx.recommended_actions.map((a, i) => (
                  <li key={i} className="text-sm text-slate-800 flex gap-1.5" data-testid={`ai-action-${i}`}>
                    <span className="text-fuchsia-600 font-black">›</span>
                    <span className="flex-1">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(dx.safety_flags || []).length > 0 && (
            <div className="mt-2 p-2 border-l-4 border-red-500 bg-red-50" data-testid="ai-safety-flags">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-800 flex items-center gap-1">
                <ShieldWarning weight="fill" size={12}/> Safety
              </div>
              <ul className="mt-1 space-y-0.5">
                {dx.safety_flags.map((s, i) => (
                  <li key={i} className="text-xs text-red-900" data-testid={`ai-safety-${i}`}>· {s}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] font-black uppercase tracking-widest text-slate-400">
            <Info size={11}/> {dx.photo_count || 0} photo{dx.photo_count === 1 ? "" : "s"} analyzed
            {dx.diagnosed_at && <span>· {new Date(dx.diagnosed_at).toLocaleString()}</span>}
            {dx.diagnosed_by_name && <span>· by {dx.diagnosed_by_name}</span>}
            {!disabled && (
              <button onClick={run} disabled={busy} data-testid="auto-diagnose-rerun"
                className="ml-auto inline-flex items-center gap-1 border-2 border-fuchsia-600 hover:bg-fuchsia-600 hover:text-white text-fuchsia-700 bg-white px-2 py-1 text-[11px] tracking-widest disabled:opacity-50 transition-colors">
                <ArrowsClockwise size={12}/> {busy ? "Rerunning…" : "Rerun"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
